/** Paramètres → Modèles : un gabarit A4 pour devis, facture, avoir. */
import { useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { CheckField } from "../../components/CheckField";
import { Field } from "../../components/Field";
import { useCompany } from "../../company/CompanyProvider";
import { MODEL_SKINS, modelBlockColor, modelFromCompany, type CompanyModel, type PageAlign } from "../../model/types";
import { ModelPreview, type ModelDocKind } from "./ModelPreview";
import { isCompanyOwner } from "./owner";

const KINDS: { id: ModelDocKind; label: string }[] = [
  { id: "invoice", label: "Facture" },
  { id: "quote", label: "Devis" },
  { id: "credit_note", label: "Avoir" },
];

function ColorField({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  disabled: boolean;
  onChange: (hex: string) => void;
}) {
  const [text, setText] = useState(value);

  useEffect(() => {
    setText(value);
  }, [value]);

  return (
    <label>
      {label}
      <span className="fx-mdl-color">
        <input
          type="color"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
        <input
          type="text"
          className="fx-mdl-hex"
          spellCheck={false}
          maxLength={7}
          value={text}
          disabled={disabled}
          aria-label={`${label}, code hexadécimal`}
          onChange={(e) => {
            const raw = e.target.value;
            setText(raw);
            const hex = raw.trim().replace(/^#/, "");
            if (/^[0-9a-fA-F]{6}$/.test(hex)) onChange(`#${hex.toLowerCase()}`);
          }}
          onBlur={() => setText(value)}
        />
      </span>
    </label>
  );
}

export function ModelTab() {
  const { company, updateModel } = useCompany();
  const [draft, setDraft] = useState<CompanyModel | null>(null);
  const [kind, setKind] = useState<ModelDocKind>("invoice");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!company) return;
    setDraft(modelFromCompany(company));
    setMsg("");
    setErr("");
  }, [company]);

  if (!company || !draft) {
    return <p className="text-sm" style={{ color: "var(--fx-muted)" }}>Aucune société.</p>;
  }

  const owner = isCompanyOwner(company);

  function set<K extends keyof CompanyModel>(key: K, value: CompanyModel[K]) {
    setDraft((cur) => (cur ? { ...cur, [key]: value } : cur));
  }

  async function save() {
    if (!draft) return;
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      await updateModel(company.id, draft);
      setMsg("Enregistré.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fx-mdl">
      <div className="fx-co-card">
        <header className="fx-co-card__head">
          <h2>Modèle</h2>
          {!owner ? (
            <p className="fx-co-form__hint">Lecture seule — seul le propriétaire enregistre le modèle.</p>
          ) : null}
        </header>

        <div className="fx-mdl-skins" role="radiogroup" aria-label="Modèle">
          {MODEL_SKINS.map((s) => (
            <button
              key={s.id}
              type="button"
              role="radio"
              aria-checked={draft.skin === s.id}
              className={draft.skin === s.id ? "is-on" : ""}
              disabled={!owner}
              onClick={() => set("skin", s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="fx-mdl-look">
          <div>
            <span>Couleurs</span>
            <div className="fx-mdl-colors">
              <ColorField
                label="Principale"
                value={draft.color_primary}
                disabled={!owner}
                onChange={(hex) => set("color_primary", hex)}
              />
              <ColorField
                label="Secondaire"
                value={draft.color_secondary}
                disabled={!owner}
                onChange={(hex) => set("color_secondary", hex)}
              />
              {draft.skin !== "aurelia" ? (
                <ColorField
                  label="Fonds"
                  value={modelBlockColor(draft)}
                  disabled={!owner}
                  onChange={(hex) => set("color_block", hex)}
                />
              ) : null}
            </div>
            {draft.skin !== "aurelia" ? (
              <p className="fx-co-form__hint">Derrière le logo, les coordonnées et les totaux.</p>
            ) : null}
          </div>
          <div>
            <span>Texte</span>
            <Field
              label="Police"
              type="number"
              min={11}
              max={16}
              value={String(draft.font_size)}
              onChange={(e) => set("font_size", Math.min(16, Math.max(11, Number(e.target.value) || 12)))}
            />
          </div>
          <div>
            <span>Logo</span>
            <Field
              label="Taille %"
              type="number"
              min={60}
              max={100}
              value={String(draft.logo_pct)}
              onChange={(e) => set("logo_pct", Math.min(100, Math.max(60, Number(e.target.value) || 100)))}
            />
          </div>
        </div>
        <div className="fx-mdl-opt-grid">
          <div>
            <span>Émis par</span>
            <div className="fx-mdl-checks">
              <CheckField label="Nom" checked disabled onChange={() => undefined} />
              <CheckField label="Adresse" checked disabled onChange={() => undefined} />
              <CheckField label="SIRET" checked disabled onChange={() => undefined} />
              <CheckField label="TVA" checked disabled onChange={() => undefined} />
              <CheckField
                label="Téléphone"
                checked={draft.show_issuer_phone}
                disabled={!owner}
                onChange={(next) => set("show_issuer_phone", next)}
              />
              <CheckField
                label="E-mail"
                checked={draft.show_issuer_email}
                disabled={!owner}
                onChange={(next) => set("show_issuer_email", next)}
              />
              <CheckField
                label="IBAN"
                checked={draft.show_issuer_iban}
                disabled={!owner}
                onChange={(next) => set("show_issuer_iban", next)}
              />
            </div>
          </div>
          <div>
            <span>Facturé à</span>
            <div className="fx-mdl-checks">
              <CheckField label="Nom" checked disabled onChange={() => undefined} />
              <CheckField label="Adresse" checked disabled onChange={() => undefined} />
              <CheckField label="SIRET" checked disabled onChange={() => undefined} />
              <CheckField label="TVA" checked disabled onChange={() => undefined} />
              <CheckField
                label="Téléphone"
                checked={draft.show_client_phone}
                disabled={!owner}
                onChange={(next) => set("show_client_phone", next)}
              />
              <CheckField
                label="E-mail"
                checked={draft.show_client_email}
                disabled={!owner}
                onChange={(next) => set("show_client_email", next)}
              />
              <CheckField
                label="Contacts"
                checked={draft.show_client_contacts}
                disabled={!owner}
                onChange={(next) => set("show_client_contacts", next)}
              />
              <CheckField
                label="Livraison (devis)"
                checked={draft.ship_address}
                disabled={!owner}
                onChange={(next) => set("ship_address", next)}
              />
            </div>
          </div>
        </div>
        <div className="fx-mdl-opt-extra">
          <span>Options</span>
          <div className="fx-mdl-checks">
            <CheckField
              label="Timbre Payé sur les factures réglées"
              checked={draft.paid_stamp}
              disabled={!owner}
              onChange={(next) => set("paid_stamp", next)}
            />
            <CheckField
              label="Numéros de page"
              checked={draft.page_numbers}
              disabled={!owner}
              onChange={(next) => set("page_numbers", next)}
            />
            <CheckField
              label="Mention « Édité avec Aurélia Factures » en bas de page"
              checked={draft.brand_mark}
              disabled={!owner}
              onChange={(next) => set("brand_mark", next)}
            />
            <label className="fx-field flex flex-col gap-1 text-xs">
              Alignement des numéros
              <select
                className="rounded-md border px-2.5 py-2 text-sm"
                value={draft.page_align}
                disabled={!draft.page_numbers}
                onChange={(e) => set("page_align", e.target.value as PageAlign)}
              >
                <option value="left">Gauche</option>
                <option value="center">Centre</option>
                <option value="right">Droite</option>
              </select>
            </label>
          </div>
        </div>

        {err ? <p className="fx-co-form__err">{err}</p> : null}
        {msg ? <p className="fx-co-form__ok">{msg}</p> : null}
        <div className="fx-co-card__foot">
          <Button disabled={busy || !owner} onClick={() => void save()}>
            Enregistrer
          </Button>
        </div>
      </div>

      <aside className="fx-mdl-side">
        <div className="fx-co-tabs" role="tablist" aria-label="Aperçu pièce">
          {KINDS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={kind === t.id}
              className={kind === t.id ? "is-on" : ""}
              onClick={() => setKind(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <ModelPreview company={company} draft={draft} kind={kind} />
      </aside>
    </div>
  );
}
